import React, { useCallback, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  FIRST_PAGE,
  TemplateChangeKind,
  TemplateRequestStatus,
} from '../../FormChangeHistory.constants';
import { PreviewPanesPropType } from '../../FormChangeHistory.propTypes';
import { getPreviewMarks } from '../../FormChangeHistory.utils';
import PreviewChangesPanel from '../PreviewChangesPanel';
import PreviewPagePane from '../PreviewPagePane';
import PreviewPageRail from '../PreviewPageRail';

import usePreviewDocument from './hooks/usePreviewDocument';
import { getPagedPreviewStyles, previewPanesStyles } from './PagedTemplatePreview.styles';
import {
  getMarkCountsByPage,
  getMarkPlacements,
  getMarksOnPage,
  getOtherMarkedPages,
  getPaneMarks,
  getUnplacedMarks,
} from './PagedTemplatePreview.utils';

/**
 * A paged template's two sides at the same page, picked from a page rail. For a Prism template the
 * changed parts are numbered on both pages and listed beside them; the one picked from the list is
 * outlined on both pages until it is picked again or the page changes.
 */
const PagedTemplatePreview = ({ auditId, panes, templateKind, parts = null }) => {
  const [olderPane, newerPane] = panes;
  const older = usePreviewDocument({ auditId, pane: olderPane, templateKind });
  const newer = usePreviewDocument({ auditId, pane: newerPane, templateKind });

  const [selectedPage, setSelectedPage] = useState(FIRST_PAGE);
  const [highlightedMarkId, setHighlightedMarkId] = useState(null);

  const onSelectPage = useCallback(pageNumber => {
    setSelectedPage(pageNumber);
    setHighlightedMarkId(null);
  }, []);

  const onToggleMark = useCallback(
    markId => setHighlightedMarkId(current => (current === markId ? null : markId)),
    [],
  );

  const sides = [older, newer];
  const hasMarks = templateKind === TemplateChangeKind.PRISM && !_.isEmpty(parts);
  const isLoading = sides.some(({ status }) => status === TemplateRequestStatus.LOADING);
  const isLayoutMissing = sides.some(({ document }) => !!document && !document.layout);
  const pageCount = _.max(sides.map(({ document }) => document?.pageSizes.length ?? 0));

  const olderLayout = older.document?.layout ?? null;
  const newerLayout = newer.document?.layout ?? null;

  const placedMarks = useMemo(
    () => (hasMarks ? getMarkPlacements(getPreviewMarks(parts), [olderLayout, newerLayout]) : []),
    [hasMarks, parts, olderLayout, newerLayout],
  );
  const markCounts = useMemo(() => getMarkCountsByPage(placedMarks), [placedMarks]);

  return (
    <Box sx={getPagedPreviewStyles(hasMarks)}>
      <PreviewPageRail
        markCounts={markCounts}
        pageCount={pageCount}
        selectedPage={selectedPage}
        onSelectPage={onSelectPage}
      />
      <Box sx={previewPanesStyles}>
        {panes.map((pane, index) => (
          <PreviewPagePane
            document={sides[index].document}
            highlightedMarkId={highlightedMarkId}
            key={pane.side}
            marks={getPaneMarks(placedMarks, index, selectedPage)}
            pageNumber={selectedPage}
            side={pane.side}
            status={sides[index].status}
            title={pane.title}
            onRetry={sides[index].retry}
          />
        ))}
      </Box>
      {hasMarks && (
        <PreviewChangesPanel
          highlightedMarkId={highlightedMarkId}
          isLayoutMissing={isLayoutMissing}
          isLoading={isLoading}
          marksOnPage={getMarksOnPage(placedMarks, selectedPage)}
          otherPages={getOtherMarkedPages(markCounts, selectedPage)}
          pageNumber={selectedPage}
          unplacedMarks={getUnplacedMarks(placedMarks)}
          onSelectPage={onSelectPage}
          onToggleMark={onToggleMark}
        />
      )}
    </Box>
  );
};

PagedTemplatePreview.propTypes = {
  auditId: PropTypes.string.isRequired,
  panes: PreviewPanesPropType.isRequired,
  templateKind: PropTypes.string.isRequired,
  parts: PropTypes.arrayOf(PropTypes.object),
};

export default PagedTemplatePreview;
